import { createContext, useContext, useCallback, useRef, useSyncExternalStore, useMemo } from 'react';

interface DialogActions {
  setDialogOpen: (dialogId: string, isOpen: boolean) => void;
  subscribe: (dialogId: string, callback: () => void) => () => void;
  getSnapshot: (dialogId: string) => boolean;
}

export const DialogContext = createContext<DialogActions | undefined>(undefined);

export function DialogProvider({ children }: { children: React.ReactNode }) {
  // Store dialog state in ref (no React state at provider level)
  const dialogStateRef = useRef<Record<string, boolean>>({});
  
  // Store subscribers for each dialog ID - key: dialogId, value: Set of callbacks
  const subscribersRef = useRef<Map<string, Set<() => void>>>(new Map());

  // Stable function to set dialog open state
  const setDialogOpen = useCallback((dialogId: string, isOpen: boolean) => {
    const previousState = dialogStateRef.current[dialogId];
    
    // Update state in ref
    dialogStateRef.current = {
      ...dialogStateRef.current,
      [dialogId]: isOpen
    };
    
    // Only notify subscribers of THIS specific dialog if state actually changed
    if (previousState !== isOpen) {
      const subscribers = subscribersRef.current.get(dialogId);
      if (subscribers) {
        // Notify only the subscribers watching this specific dialogId
        subscribers.forEach(callback => callback());
      }
    }
  }, []);

  // Stable subscribe function for a specific dialog ID
  const subscribe = useCallback((dialogId: string, callback: () => void) => {
    // Get or create subscriber set for this dialog ID
    if (!subscribersRef.current.has(dialogId)) {
      subscribersRef.current.set(dialogId, new Set());
    }
    
    const subscribers = subscribersRef.current.get(dialogId)!;
    subscribers.add(callback);
    
    // Return unsubscribe function
    return () => {
      subscribers.delete(callback);
      // Clean up empty sets to prevent memory leaks
      if (subscribers.size === 0) {
        subscribersRef.current.delete(dialogId);
      }
    };
  }, []);

  // Stable snapshot getter for a specific dialog ID
  const getSnapshot = useCallback((dialogId: string) => {
    return dialogStateRef.current[dialogId] || false;
  }, []);

  // Memoize context value - all functions are stable
  const value = useMemo(() => ({ 
    setDialogOpen, 
    subscribe, 
    getSnapshot 
  }), [setDialogOpen, subscribe, getSnapshot]);

  return (
    <DialogContext.Provider value={value}>
      {children}
    </DialogContext.Provider>
  );
}

// DEPRECATED: Legacy hook for backward compatibility - DO NOT USE IN NEW CODE
// This hook is NON-REACTIVE and will NOT cause re-renders when dialog state changes
// Use useDialogOpen(dialogId) instead for reactive behavior
export function useDialogState() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialogState must be used within a DialogProvider');
  }
  
  // Emit deprecation warning in development
  if (import.meta.env.DEV) {
    console.warn(
      '[DEPRECATED] useDialogState() is deprecated and NON-REACTIVE. ' +
      'Use useDialogOpen(dialogId) instead for reactive dialog state management. ' +
      'Components using useDialogState() will NOT re-render when dialog state changes.'
    );
  }
  
  // Return wrapper that provides the old API
  // WARNING: isDialogOpen is NOT reactive - it won't trigger re-renders
  return {
    setDialogOpen: context.setDialogOpen,
    isDialogOpen: (dialogId: string) => context.getSnapshot(dialogId),
  };
}

// New hook to reactively watch a specific dialog's open state
// This hook uses useSyncExternalStore to subscribe ONLY to the specific dialogId
// Opening Dialog A will NOT cause components watching Dialog B to re-render
export function useDialogOpen(dialogId: string): [boolean, (isOpen: boolean) => void] {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialogOpen must be used within a DialogProvider');
  }
  
  const { setDialogOpen, subscribe, getSnapshot } = context;
  
  // Subscribe to this specific dialog only using useSyncExternalStore
  // This ensures the component re-renders ONLY when THIS dialog's state changes
  const isOpen = useSyncExternalStore(
    useCallback((callback) => subscribe(dialogId, callback), [subscribe, dialogId]),
    useCallback(() => getSnapshot(dialogId), [getSnapshot, dialogId])
  );
  
  // Stable setter for this specific dialog
  const setOpen = useCallback((isOpen: boolean) => {
    setDialogOpen(dialogId, isOpen);
  }, [setDialogOpen, dialogId]);
  
  return [isOpen, setOpen];
}
